import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Switch } from 'antd';
import { BreadCrumbs, UserInfo } from 'components';
import { Flex, Text } from 'atoms';
import { getLocalData } from 'hooks/useAuth';

const Container = styled(Flex)`
	align-items: center;
	justify-content: space-between;
	height: 80px;
	background: white;
	padding-right: 4rem;
	min-height: 64px;
	position: fixed;
	width: calc(100% - 240px);
	z-index: 1000;
	top: 0;
	left: 240px;
	border-bottom: 1px solid #e0e0e3;
`;

const UIToggleContainer = styled(Flex)`
	align-items: center;
	gap: 8px;
	margin-right: 16px;
`;

const UIToggleLabel = styled(Text)`
	font-size: 14px;
	color: #595959;
	font-weight: 400;
`;

const UI_VERSION_KEY = 'uiVersion';

// Only these roles can see and use the old/new UI toggle
const UI_TOGGLE_ALLOWED_ROLES = ['finance', 'control_tower', 'admin'];

export const getUIVersion = () => {
	const userType = getLocalData()?.user_type;
	if (!UI_TOGGLE_ALLOWED_ROLES.includes(userType)) {
		// Non-allowed roles always get new UI; normalise localStorage too
		if (localStorage.getItem(UI_VERSION_KEY) !== 'new') {
			localStorage.setItem(UI_VERSION_KEY, 'new');
		}
		return 'new';
	}
	return localStorage.getItem(UI_VERSION_KEY) || 'old';
};

export const setUIVersion = (version) => {
	localStorage.setItem(UI_VERSION_KEY, version);
	// Trigger a custom event to notify router-static-data to update
	window.dispatchEvent(new CustomEvent('uiVersionChanged', { detail: { version } }));
};

export default function Header({ urls = {} }) {
	const [uiVersion, setUiVersionState] = React.useState(getUIVersion);
	const canToggleUI = UI_TOGGLE_ALLOWED_ROLES.includes(getLocalData()?.user_type);

	const handleToggle = (checked) => {
		const newVersion = checked ? 'new' : 'old';
		const currentPath = window.location.pathname;

		// List of v2-only routes
		const v2OnlyRoutes = ['/nodes', '/routes'];

		// List of old UI-only routes
		const oldUIOnlyRoutes = ['/approvals'];

		// Check if current route is v2-only
		const isOnV2OnlyRoute = v2OnlyRoutes.some((route) => currentPath.startsWith(route));

		// Check if current route is old UI-only
		const isOnOldUIOnlyRoute = oldUIOnlyRoutes.some(
			(route) => currentPath === route || currentPath.startsWith(`${route}/`)
		);

		// If switching to old UI and currently on a v2-only page, redirect to dashboard
		if (newVersion === 'old' && isOnV2OnlyRoute) {
			setUIVersion(newVersion);
			window.location.href = '/dashboard';
			return;
		}

		// If switching to new UI and currently on an old UI-only page, redirect to dashboard
		if (newVersion === 'new' && isOnOldUIOnlyRoute && currentPath === '/approvals') {
			setUIVersion(newVersion);
			window.location.href = '/dashboard';
			return;
		}

		setUIVersion(newVersion);
		setUiVersionState(newVersion);
		// Reload the page to apply the new UI
		window.location.reload();
	};

	React.useEffect(() => {
		// Listen for changes from other tabs/windows
		const handleStorageChange = (e) => {
			if (e.key === UI_VERSION_KEY) {
				setUiVersionState(e.newValue || 'old');
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, []);

	return (
		<Container>
			<BreadCrumbs urls={urls} />
			<Flex alignItems="center">
				{canToggleUI && (
					<UIToggleContainer>
						<UIToggleLabel>Old UI</UIToggleLabel>
						<Switch
							checked={uiVersion === 'new'}
							onChange={handleToggle}
							checkedChildren="New"
							unCheckedChildren="Old"
						/>
						<UIToggleLabel>New UI</UIToggleLabel>
					</UIToggleContainer>
				)}
				<UserInfo />
			</Flex>
		</Container>
	);
}

Header.propTypes = {
	urls: PropTypes.shape({
		id: PropTypes.number,
		text: PropTypes.string,
		link: PropTypes.string,
	}),
};
